import {Separator, useFormContext} from 'platform/components';
import {VStack} from 'platform/foundation';

import {Controller} from 'react-hook-form';

import {suffixTestId, TestIdProps} from 'shared/web';

import {ALLOWED_SELLER_TYPE_KEYS} from '../../../../../constants/allowedSellerTypeKeys';
import {useCatalogue} from '../../../../../hooks/useCatalogue';
import i18n from '../../../../../i18n/i18n';
import {SeparatorWithLabel} from '../../../../SeparatorWithLabel';
import {PresetEditorFormValues} from '../utils/presetEditorFormSchema';
import {PresetFeaturesField} from './PresetFeaturesField';
import {SellerTypePropertyChips} from './SellerTypePropertyChips';
import {ThreeWayPropertyChips} from './ThreeWayPropertyChips';
import {TwoWayPropertyChips} from './TwoWayPropertyChips';

interface PropertiesSectionProps extends TestIdProps {
  isReadOnly?: boolean;
}

export function PropertiesSection(props: PropertiesSectionProps) {
  const {isReadOnly} = props;
  const {control} = useFormContext<PresetEditorFormValues>();
  const {sellerTypeOptions} = useCatalogue();

  const sellerTypeChipOptions =
    sellerTypeOptions?.filter((option) => ALLOWED_SELLER_TYPE_KEYS.includes(option.value)) ?? [];

  return (
    <VStack spacing={2}>
      <SeparatorWithLabel label={i18n.t('entity.filterForm.savedFilters.dialog.properties')} />

      <Controller
        name="trimLevel"
        control={control}
        render={({field}) => (
          <ThreeWayPropertyChips
            label={i18n.t('entity.filterForm.featureTiers')}
            value={field.value}
            onChange={field.onChange}
            sameAndBetterHint={i18n.t('entity.filterForm.savedFilters.hint.trimSameAndBetter')}
            isReadOnly={isReadOnly}
          />
        )}
      />

      <Separator spacing={1} />

      <Controller
        name="transmission"
        control={control}
        render={({field}) => (
          <ThreeWayPropertyChips
            label={i18n.t('entity.filterForm.transmission')}
            value={field.value}
            onChange={field.onChange}
            sameAndBetterHint={i18n.t(
              'entity.filterForm.savedFilters.hint.transmissionSameAndBetter'
            )}
            isReadOnly={isReadOnly}
          />
        )}
      />

      <Separator spacing={1} />

      <Controller
        name="fuel"
        control={control}
        render={({field}) => (
          <TwoWayPropertyChips
            label={i18n.t('entity.filterForm.fuel')}
            value={field.value}
            onChange={field.onChange}
            isReadOnly={isReadOnly}
          />
        )}
      />

      <Separator spacing={1} />

      <Controller
        name="body"
        control={control}
        render={({field}) => (
          <TwoWayPropertyChips
            label={i18n.t('entity.filterForm.body')}
            value={field.value}
            onChange={field.onChange}
            isReadOnly={isReadOnly}
          />
        )}
      />

      <Separator spacing={1} />

      <Controller
        name="drive"
        control={control}
        render={({field}) => (
          <ThreeWayPropertyChips
            label={i18n.t('entity.filterForm.propulsion')}
            value={field.value}
            onChange={field.onChange}
            sameAndBetterHint={i18n.t('entity.filterForm.savedFilters.hint.driveSameAndBetter')}
            isReadOnly={isReadOnly}
          />
        )}
      />

      <Separator spacing={1} />

      <Controller
        name="vatDeductible"
        control={control}
        render={({field}) => (
          <ThreeWayPropertyChips
            label={i18n.t('entity.filterForm.VATDeductible')}
            value={field.value}
            onChange={field.onChange}
            sameAndBetterHint={i18n.t('entity.filterForm.savedFilters.hint.vatSameAndBetter')}
            isReadOnly={isReadOnly}
          />
        )}
      />

      <Separator spacing={1} />

      <Controller
        name="sellerType"
        control={control}
        render={({field}) => (
          <SellerTypePropertyChips
            label={i18n.t('entity.table.sellerType')}
            value={field.value}
            onChange={field.onChange}
            options={sellerTypeChipOptions}
            isReadOnly={isReadOnly}
          />
        )}
      />

      <Separator spacing={1} />

      <Controller
        name="seats"
        control={control}
        render={({field}) => (
          <ThreeWayPropertyChips
            label={i18n.t('entity.filterForm.seats')}
            value={field.value}
            onChange={field.onChange}
            isReadOnly={isReadOnly}
          />
        )}
      />

      <Separator spacing={1} />

      <PresetFeaturesField isReadOnly={isReadOnly} data-testid={suffixTestId('features', props)} />
    </VStack>
  );
}
